import React from 'react';

import { Menu, MenuItem } from '@change/ds-core-ui';
import { MoreHorizontalIcon, ReportIcon, TrashFilledIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core/react/i18n';

import { useOpenReportAbuseModal } from 'src/app/shared/components/abuseSunrise';

import type { PageRenderingButton, SupporterVideo } from '../../shared/types';
import { useDeleteVideo } from '../DeleteVideo/hooks/useDeleteVideo';
import { useReportVideo } from '../ReportVideo/hooks/useReportVideo';

type VideoComponentProps = {
	video: SupporterVideo;
	petitionId: string;
	removeVideoFromState: (videoId?: string) => void;
	petitionSlug: string;
	renderedOn?: PageRenderingButton;
};

function getDeleteAriaLabel(
	translate: (key: string, params?: { user_name: string }) => string,
	userName: string,
): string {
	return translate('corgi.components.petition_video.actions.aria_label.delete_video', {
		user_name: userName,
	});
}

function getReportAriaLabel(
	translate: (key: string, params?: { user_name: string }) => string,
	userName: string,
): string {
	return translate('corgi.components.petition_video.actions.aria_label.report_video', {
		user_name: userName,
	});
}

export const MoreActionsVideoMenu: React.FC<VideoComponentProps> = ({
	video,
	petitionId,
	removeVideoFromState,
	petitionSlug,
	renderedOn = 'petition_page',
}) => {
	const {
		data: { canDeleteVideo },
		actions: { handleDeleteClick },
	} = useDeleteVideo({ petitionId, video, afterDeleteAction: removeVideoFromState, renderedOn });

	const {
		data: { canReportVideo },
		actions: { handleReportClick },
	} = useReportVideo({ petitionId, video });

	const { open: openReportAbuseModal, reportAbuseModals } = useOpenReportAbuseModal({
		reportContext: {
			type: video.videoUrl ? 'video_comment' : 'comment',
			comment: { id: video.id, likes: Number(video.likeCount), role: 'VIDEO_COMMENT' },
			petitionId,
			petitionSlug,
		},
	});

	const { translate } = useI18n();

	const triggerMoreActionsAriaLabel = translate('corgi.components.petition_video.actions.aria_label.more_actions');
	const userName = video.user?.shortDisplayName ?? '';

	if (!canReportVideo && !canDeleteVideo) return null;

	const deleteAriaLabel = getDeleteAriaLabel(translate, userName);
	const reportAriaLabel = getReportAriaLabel(translate, userName);

	const handleReportAction = () => {
		handleReportClick();
		openReportAbuseModal();
	};

	return (
		<>
			<Menu
				isNonModal={true}
				placement="top left"
				triggerClassName={cn(
					'text-typography-inverse-strong',
					// layout
					'flex items-center p-3',
					// border
					'rounded-medium hover:border-stroke-weaker border border-transparent',
					// background
					'hover:bg-fill-hover focus-visible:bg-fill-hover',
				)}
				triggerContent={<MoreHorizontalIcon />}
				contentClassName="p-2 -mb-3 rounded-large!"
				triggerAriaLabel={triggerMoreActionsAriaLabel}
				triggerQaAttribute="more-actions-menu-button"
			>
				<>
					{canDeleteVideo && (
						<MenuItem
							data-testid={`supporter-video-${video.id}-delete-button`}
							key="delete-video-menu-item"
							className="[&_svg]:h-4 [&_svg]:w-4"
							aria-label={deleteAriaLabel}
							label="Delete"
							leadingIcon={<TrashFilledIcon />}
							onAction={handleDeleteClick}
						/>
					)}

					{canReportVideo && (
						<MenuItem
							key="report-video-menu-item"
							className="[&_svg]:h-4 [&_svg]:w-4"
							onAction={handleReportAction}
							label="Report"
							leadingIcon={<ReportIcon />}
							aria-label={reportAriaLabel}
							data-testid={`supporter-video-${video.id}-report-button`}
							data-qa={`report-abuse-${video.videoUrl ? 'video_comment' : 'comment'}`}
						/>
					)}
				</>
			</Menu>
			{reportAbuseModals}
		</>
	);
};
